import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, ArrowLeft, Crown, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PLANS, planLookup } from "@/lib/plans";

export default function Billing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [billing, setBilling] = useState(() => {
    try { return localStorage.getItem("sg_intended_billing") === "annual" ? "annual" : "monthly"; } catch (e) { return "monthly"; }
  });
  const [loadingKey, setLoadingKey] = useState("");
  const [portalBusy, setPortalBusy] = useState(false);
  const [state, setState] = useState(user?.subscription_state || null);

  useEffect(() => { api.get("/subscription").then(({ data }) => setState(data)).catch(() => {}); }, []);

  const currentPlan = state?.plan;
  const paidPlans = PLANS.filter((p) => !p.isTrial);

  const openPortal = async () => {
    setPortalBusy(true);
    try {
      const { data } = await api.post("/payments/portal", { origin_url: window.location.origin });
      window.location.href = data.portal_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Portale non disponibile");
      setPortalBusy(false);
    }
  };

  const checkout = async (planId) => {
    const lookup = planLookup(planId, billing);
    setLoadingKey(lookup);
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Impossibile avviare il pagamento");
      setLoadingKey("");
    }
  };

  return (
    <div className="px-4 pt-6 pb-10">
      <button onClick={() => navigate(-1)} className="flex items-center gap-1 text-sm text-slate-500 mb-4" data-testid="billing-back"><ArrowLeft className="w-4 h-4" /> Indietro</button>
      <h1 className="font-display font-extrabold text-2xl tracking-tight text-slate-900">Abbonamento</h1>
      <p className="text-slate-500 mt-1 text-sm">
        {currentPlan === "trial"
          ? (state?.expired ? "La tua prova è terminata. Scegli un piano per continuare." : "Sei in prova gratuita. Passa a un piano quando vuoi.")
          : currentPlan === "canceled"
            ? "Il tuo abbonamento è stato annullato. Scegli un piano per riattivarlo."
            : <>Piano attuale: <span className="font-semibold text-blue-600 capitalize">{currentPlan}</span></>}
      </p>
      {state?.current_period_end && ["pro", "business"].includes(currentPlan) && (
        <p className="text-xs text-slate-400 mt-1" data-testid="billing-renewal">Prossimo rinnovo: {new Date(state.current_period_end).toLocaleDateString("it-IT")}</p>
      )}
      {state?.status === "past_due" && (
        <div className="mt-3 rounded-2xl border border-amber-200 bg-amber-50 p-3" data-testid="billing-pastdue">
          <p className="text-sm font-semibold text-amber-900">Pagamento non riuscito</p>
          <p className="text-xs text-amber-700 mt-0.5">Aggiorna il metodo di pagamento per non perdere l'accesso alle funzioni del tuo piano.</p>
        </div>
      )}

      <div className="mt-5 inline-flex items-center bg-slate-100 rounded-full p-1">
        <button data-testid="billing-cycle-monthly" onClick={() => setBilling("monthly")}
          className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors ${billing === "monthly" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>Mensile</button>
        <button data-testid="billing-cycle-annual" onClick={() => setBilling("annual")}
          className={`rounded-full px-4 py-2 text-sm font-semibold transition-colors inline-flex items-center gap-2 ${billing === "annual" ? "bg-white text-slate-900 shadow-sm" : "text-slate-500"}`}>
          Annuale <span className="text-[10px] font-bold bg-blue-100 text-blue-700 rounded-full px-2 py-0.5">2 mesi gratis</span>
        </button>
      </div>

      <div className="mt-6 space-y-4">
        {paidPlans.map((plan) => {
          const isCurrent = currentPlan === plan.id;
          const lookup = planLookup(plan.id, billing);
          return (
            <div key={plan.id} data-testid={`billing-plan-${plan.id}`}
              className={`relative rounded-3xl border-2 p-6 bg-white ${plan.recommended ? "border-blue-500 shadow-lg shadow-blue-500/10" : "border-slate-200"}`}>
              {plan.badge && <span className="absolute -top-3 left-6 bg-blue-500 text-white text-xs font-bold rounded-full px-3 py-1">{plan.badge}</span>}
              <div className="flex items-baseline justify-between">
                <p className="font-display font-bold text-lg text-slate-900">{plan.name}</p>
                <div className="text-right"><span className="font-display font-extrabold text-3xl text-slate-900">{plan.priceLabel[billing]}</span><span className="text-slate-400 text-sm">/{billing === "annual" ? "anno" : "mese"}</span></div>
              </div>
              <p className="text-sm text-slate-500 mt-2">{plan.description}</p>
              <ul className="mt-4 space-y-2">
                {plan.features.slice(0, 6).map((f) => (
                  <li key={f} className="flex gap-2 text-sm text-slate-700"><Check className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />{f}</li>
                ))}
              </ul>
              {isCurrent ? (
                <div className="mt-5 w-full text-center rounded-full py-3 font-semibold bg-blue-50 text-blue-600" data-testid={`billing-current-${plan.id}`}>Piano attuale ✓</div>
              ) : (
                <button onClick={() => checkout(plan.id)} disabled={!!loadingKey} data-testid={`billing-checkout-${plan.id}`}
                  className="mt-5 w-full inline-flex items-center justify-center gap-2 rounded-full py-3 font-semibold bg-blue-500 text-white shadow-lg shadow-blue-500/25 hover:bg-blue-600 disabled:opacity-60">
                  {loadingKey === lookup ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Crown className="w-4 h-4" /> Scegli {plan.name}</>}
                </button>
              )}
            </div>
          );
        })}
      </div>

      {["pro", "business"].includes(currentPlan) && (
        <button onClick={openPortal} disabled={portalBusy} data-testid="billing-portal"
          className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full py-3.5 font-semibold border border-slate-300 text-slate-800 hover:bg-slate-50 disabled:opacity-60">
          {portalBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Crown className="w-4 h-4" /> Gestisci o annulla su Stripe</>}
        </button>
      )}

      <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex items-center gap-2 text-slate-500"><Sparkles className="w-4 h-4" /><p className="text-sm font-semibold">Pagamento con PayPal</p></div>
        <p className="text-xs text-slate-400 mt-1">In arrivo — attiveremo il pagamento con PayPal a breve.</p>
        <button disabled data-testid="billing-paypal-soon" className="mt-3 w-full rounded-full py-2.5 text-sm font-semibold border border-slate-200 text-slate-400 cursor-not-allowed">PayPal — presto disponibile</button>
      </div>

      <p className="text-xs text-slate-400 text-center mt-5">Pagamenti sicuri con Stripe. Puoi annullare quando vuoi. Carta di test: 4242 4242 4242 4242.</p>
    </div>
  );
}
